import context from './context'
import versioning from './versioning'
import CookieConsentStorage from './local/cookie-consent-storage'
import SessionStorage from './local/session-storage'

import Logger from '@/logger'

import StorageHelper from './helper'

import Store from '@/store'
import { MUTATIONS } from '@/store/mutations'
import { ACTIONS } from '@/store/actions'

/**
 * Storage access methods.
 */
const Storage = {

    context,
    versioning,

    /**
     * @type {CookieConsentStorage}
     */
    consent: new CookieConsentStorage(),

    /**
     * @type {SessionStorage}
     */
    session: new SessionStorage(),

    /**
     * Clears the contextual information, triggering a potential new user state.
     *
     * @param {Object} { clearData = false, keepProperty: false } Additional tasks to perform.
     * @returns {Promise<Object>} { clearContext: true, clearData: true } The clear responses.
     */
    async clearContextAsync({ clearData = false, keepProperty = false } = { clearData: false, keepProperty: false }) {
        try {
            Logger.logObject({ clearData, keepProperty }, 'Ensuring authentication context. If this fails try closing the Inspection window - Vue DevTools can make it be weird.', 'Local Storage')
            const cxt = await Store.dispatch(ACTIONS.ensureAuthContext)
            if (!cxt) {
                return {
                    clearContext: false,
                    clearData: false,
                }
            }

            Logger.log(`Clearing context of ${ cxt.id || cxt.type }`, 'Local Storage')

            const clearDataResponse = clearData ? await Storage.clearDataAsync(keepProperty) : null
            const clearContextResponse = await StorageHelper.getLocalStore(cxt).clearContextAsync()

            Store.commit(MUTATIONS.setAuthContext, null)
            StorageHelper.resetAllVuexState(keepProperty)
            if (!clearData)
                Logger.log('State reset.', 'State')

            const response = {
                clearContext: clearContextResponse,
                clearData: clearDataResponse,
            }

            Logger.logObject(response, `Context cleared [clearData:${ clearData }, keepProperty:${ keepProperty }].`, 'Local Storage')
            return response
        } catch (err) {
            Logger.log('Unable to clear context', 'Local Storage')
            throw err
        }
    },

    /**
     * Clears all data relating to the current context (but not the context itself).
     *
     * @param {Boolean} keepProperty - Set to true to persist the selected property data in Vuex.
     * @returns {Promise<Boolean>} A flag to indicate completion.
     */
    async clearDataAsync(keepProperty = false) {
        try {
            const cxt = await Store.dispatch(ACTIONS.ensureAuthContext)
            Logger.log(`Clearing data for ${ cxt.id || cxt.type }`, 'Storage')

            StorageHelper.resetAllVuexState(keepProperty)
            Logger.log('Data cleared and state reset.', 'State / Storage')
            return true
        } catch (err){
            Logger.log('Unable to clear data', 'Storage')
            throw err
        }
    },

    /**
     * Clears the contextual information for a guest when the guest isn't current.
     *
     * @returns {Promise<Object>} { clearContext: true, clearData: true } The clear responses.
     */
    async clearGuestContextAsync() {
        try {
            Logger.log('Clearing guest context', 'Local Storage')

            const clearContextResponse = await StorageHelper.guestLocal.clearContextAsync()

            if (Store.state.authContext && Store.state.authContext.type == 'guest')
                Store.commit(MUTATIONS.setAuthContext, null)

            Logger.logObject(clearContextResponse, `Guest context cleared.`, 'Local Storage')
            return {
                clearContext: clearContextResponse,
                clearData: true,
            }
        } catch (err) {
            Logger.log('Unable to clear guest context', 'Local Storage')
            throw err
        }
    },

    /**
     * Clones all data relating to the current context (but not the context itself) to a different newContextId.
     * This is very useful to create a copy of contextual state information in preperation for an ID change.
     *
     * @param {string} newContextId
     * @returns {Promise<Object>} { versioningResponse: false } The clone responses.
     */
    async cloneDataAsync(newContextId) {
        try {
            const cxt = await Store.dispatch(ACTIONS.ensureAuthContext)
            let response = {
                versioningResponse: false,
            }

            if (!cxt) {
                Logger.logObject(response, `No data availabe to clone to ${ newContextId }`, 'Local Storage')
                return response
            }

            Logger.log(`Cloning data for ${ cxt.id || cxt.type } to ${ newContextId }`, 'Local Storage')
            response = await StorageHelper.getLocalStore(cxt).cloneAsync(newContextId)
            Logger.logObject(response, `Data cloned from ${ cxt.id || cxt.type } to ${ newContextId }`, 'Storage')
            return response
        } catch (err){
            Logger.log('Unable to clone data', 'Storage')
            throw err
        }
    },
}

export default Storage
